/**
 * Settings. Deliberately small: interests feed the planner's scenario choice,
 * target minutes bounds the session, and the theme toggle exists because dark
 * is the default rather than the only option.
 */

import { api, ApiError } from "../api.ts";
import { readLocal, writeLocal } from "../store.ts";
import type { SettingsResponse } from "../types.ts";
import { el, mount, primaryButton, ruleTil, shell } from "../ui.ts";
import { currentTheme, setTheme } from "../theme.ts";

export async function renderSettings(back: () => void, onLogout: () => void): Promise<void> {
	let settings: SettingsResponse | null = null;
	try {
		settings = await api.settings();
	} catch {
		settings = null;
	}

	const body: Node[] = [];

	// Theme
	body.push(el("div", { class: "meta", text: "Appearance" }));
	const themeButton = el("button", {
		class: "btn btn--ghost",
		type: "button",
		style: "margin-top:10px",
		text: currentTheme() === "dark" ? "Switch to light" : "Switch to dark",
	});
	themeButton.addEventListener("click", () => {
		const next = currentTheme() === "dark" ? "light" : "dark";
		setTheme(next);
		themeButton.textContent = next === "dark" ? "Switch to light" : "Switch to dark";
	});
	body.push(themeButton);

	body.push(ruleTil());

	// Interests
	body.push(el("div", { class: "meta", text: "Interests" }));
	body.push(
		el("p", {
			class: "en",
			style: "margin-top:6px",
			text: "Comma separated. The planner weights scenarios toward these.",
		}),
	);
	const interests = el("input", {
		class: "field",
		type: "text",
		"aria-label": "Interests",
		placeholder: "cooking, football, music",
		value: (settings?.interests ?? []).join(", "),
	});
	body.push(interests);

	// Target minutes
	body.push(el("div", { class: "meta", style: "margin-top:22px", text: "Session length" }));
	const minutes = el("input", {
		class: "field",
		type: "number",
		min: "5",
		max: "45",
		"aria-label": "Target minutes",
		value: String(settings?.target_minutes ?? 12),
	});
	body.push(minutes);

	const status = el("div", { class: "stack--tight", "aria-live": "polite" });
	body.push(status);

	body.push(ruleTil());
	body.push(
		el("div", { class: "meta", text: "Voices in rotation" }),
		el("p", { class: "en", style: "margin-top:6px", lang: "pt-BR", text: (settings?.voices ?? []).join(" · ") }),
	);

	body.push(ruleTil());
	body.push(resetSection(back));

	body.push(
		el(
			"div",
			{ class: "link-row" },
			el("button", {
				class: "link",
				type: "button",
				text: "Sign out",
				onclick: () => {
					void api.logout().finally(onLogout);
				},
			}),
		),
	);

	const save = primaryButton("Save", () => {
		void (async () => {
			save.disabled = true;
			save.textContent = "Saving…";
			try {
				await api.saveSettings({
					interests: interests.value
						.split(",")
						.map((value) => value.trim())
						.filter(Boolean)
						.slice(0, 8),
					target_minutes: Number(minutes.value) || 12,
				});
				writeLocal("target_minutes", Number(minutes.value) || 12);
				back();
			} catch (error) {
				status.replaceChildren(
					el("div", { class: "notice", text: error instanceof ApiError ? error.message : "Saving needs a connection." }),
				);
				save.disabled = false;
				save.textContent = "Save";
			}
		})();
	});

	mount(
		shell({
			back: { label: "Hoje", onClick: back },
			eyebrowLeft: "Ajustes",
			body,
			footer: el(
				"div",
				{},
				save,
				el("div", { style: "height:9px" }),
				primaryButton("Back to today", back, { variant: "ghost" }),
			),
		}),
	);

	// Keep the local mirror in step for the offline Today screen.
	writeLocal("interests", readLocal<string[]>("interests", settings?.interests ?? []));
}

/**
 * Starting over. Two taps, because there is no undo and the wording of the
 * first one is the only warning the learner gets.
 */
function resetSection(back: () => void): Node {
	const wrapper = el(
		"div",
		{},
		el("div", { class: "meta", text: "Start over" }),
		el("p", {
			class: "en",
			style: "margin-top:6px",
			text: "Clears every session, answer and review schedule, and puts the vocabulary back to unseen. Your interests, session length and the audio already generated are kept.",
		}),
	);

	const slot = el("div", { style: "margin-top:10px" });
	wrapper.append(slot);

	const offer = (): void => {
		slot.replaceChildren(
			el("button", {
				class: "btn btn--ghost",
				type: "button",
				text: "Reset progress…",
				onclick: confirm,
			}),
		);
	};

	const confirm = (): void => {
		const go = el("button", {
			class: "btn btn--ochre",
			type: "button",
			text: "Yes, erase everything",
		});
		go.addEventListener("click", () => {
			go.disabled = true;
			go.textContent = "Erasing…";
			void api
				.resetProgress()
				.then(() => {
					// Nothing on the Today screen survives, so go and refetch it.
					back();
				})
				.catch((error: unknown) => {
					slot.replaceChildren(
						el("div", {
							class: "notice",
							text: error instanceof ApiError ? error.message : "Reset needs a connection. Nothing was deleted.",
						}),
					);
					offer();
				});
		});

		slot.replaceChildren(
			el("div", {
				class: "notice notice--brick",
				text: "This cannot be undone. Session history, streak and review schedule all go.",
			}),
			el("div", { style: "height:9px" }),
			go,
			el("div", { style: "height:9px" }),
			el("button", { class: "link", type: "button", text: "Keep my progress", onclick: offer }),
		);
	};

	offer();
	return wrapper;
}
